import { useState } from 'react'
import { Clock, Copy, Check } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import type { WorkingHours, WorkingHoursDay } from '@/hooks/useAssistantCalendarSettings'

interface WorkingHoursEditorProps {
  value: WorkingHours
  onChange: (hours: WorkingHours) => void
  disabled?: boolean
}

const DAY_LABELS: Record<keyof WorkingHours, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terca-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sabado',
  sunday: 'Domingo'
}

const DAY_ORDER: (keyof WorkingHours)[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
]

export function WorkingHoursEditor({ value, onChange, disabled }: WorkingHoursEditorProps) {
  const [copySource, setCopySource] = useState<keyof WorkingHours | null>(null)

  const updateDay = (day: keyof WorkingHours, updates: Partial<WorkingHoursDay>) => {
    onChange({
      ...value,
      [day]: { ...value[day], ...updates }
    })
  }

  const copyToAll = (sourceDay: keyof WorkingHours) => {
    const source = value[sourceDay]
    const newHours: WorkingHours = { ...value }

    DAY_ORDER.forEach(day => {
      if (day !== sourceDay) {
        newHours[day] = { ...source }
      }
    })

    onChange(newHours)
    setCopySource(sourceDay)
    setTimeout(() => setCopySource(null), 2000)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
        <Clock className="w-4 h-4" />
        Horarios de Funcionamento
      </div>

      <div className="space-y-2">
        {DAY_ORDER.map(day => (
          <div
            key={day}
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50"
          >
            <Switch
              checked={value[day].enabled}
              onCheckedChange={(checked) => updateDay(day, { enabled: checked })}
              disabled={disabled}
            />

            <span className="w-28 text-sm text-zinc-300">
              {DAY_LABELS[day]}
            </span>

            {value[day].enabled ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={value[day].start}
                    onChange={(e) => updateDay(day, { start: e.target.value })}
                    disabled={disabled}
                    className="px-2 py-1 text-sm rounded bg-zinc-700 border border-zinc-600 text-white focus:outline-none focus:border-emerald-500"
                  />
                  <span className="text-zinc-500">ate</span>
                  <input
                    type="time"
                    value={value[day].end}
                    onChange={(e) => updateDay(day, { end: e.target.value })}
                    disabled={disabled}
                    className="px-2 py-1 text-sm rounded bg-zinc-700 border border-zinc-600 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => copyToAll(day)}
                  disabled={disabled}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-emerald-400 transition-colors disabled:opacity-50"
                  title="Copiar para todos os dias"
                >
                  {copySource === day ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">Copiar</span>
                </button>
              </>
            ) : (
              <span className="text-sm text-zinc-500 italic">Fechado</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-zinc-500">
        O assistente so podera agendar dentro destes horarios.
      </p>
    </div>
  )
}
